import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Shield,
  LogOut,
  Factory,
  QrCode,
  GitBranch,
  Workflow,
  FileText,
  Menu,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../components/ui/avatar';
import PlatformFooter from '../components/optimar/PlatformFooter';

const DashboardLayout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Auto-close drawer when route changes (mobile UX).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Batches', href: '/batches', icon: Package },
    { name: 'Compliance', href: '/compliance', icon: Shield },
  ];

  const optimarNavigation = [
    { name: 'Demo Dashboard', href: '/optimar', icon: Factory, exact: true },
    { name: 'Batch Detail', href: '/optimar/batch', icon: Workflow },
    { name: 'Compliance Report', href: '/optimar/report', icon: FileText },
    { name: 'Customer / QR View', href: '/optimar/customer', icon: QrCode },
    { name: 'Data Mapping', href: '/optimar/data-mapping', icon: GitBranch },
  ];

  const getRoleColor = (role) => {
    const roleColors = {
      supply_chain_manager: 'bg-blue-100 text-blue-700',
      sustainability_manager: 'bg-teal-100 text-teal-700',
      external_viewer: 'bg-slate-100 text-slate-700',
    };
    return roleColors[role] || 'bg-slate-100 text-slate-700';
  };

  const getRoleName = (role) => {
    const roleMap = {
      supply_chain_manager: 'Supply Chain Manager',
      sustainability_manager: 'Sustainability Manager',
      external_viewer: 'External Auditor',
    };
    return roleMap[role] || role;
  };

  const SidebarBody = () => (
    <>
      {/* Logo */}
      <div className="py-6 px-6 border-b border-slate-200 bg-white lg:py-10">
        <img
          src="https://customer-assets.emergentagent.com/job_farm-to-table-135/artifacts/jfihotrx_IMG_5685.png"
          alt="PureSustain Logo"
          className="w-full h-auto"
          style={{
            maxHeight: '200px',
            objectFit: 'contain',
            filter: 'contrast(1.35) brightness(1.15) saturate(1.2)',
            imageRendering: '-webkit-optimize-contrast',
            WebkitFontSmoothing: 'antialiased',
            opacity: 1,
          }}
        />
      </div>

      {/* Navigation */}
      <nav
        className="flex-1 p-4 space-y-2 overflow-y-auto"
        data-testid="sidebar-nav"
      >
        {navigation.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href));
          return (
            <NavLink
              key={item.name}
              to={item.href}
              data-testid={`nav-${item.name.toLowerCase()}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </NavLink>
          );
        })}

        {/* Optimar Demo section */}
        <div className="pt-4">
          <div className="px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Optimar Demo
          </div>
          <div className="mb-2 mx-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-amber-800">
            Demo data only
          </div>
          {optimarNavigation.map((item) => {
            const isActive = item.exact
              ? location.pathname === item.href
              : location.pathname === item.href ||
                location.pathname.startsWith(item.href + '/');
            return (
              <NavLink
                key={item.name}
                to={item.href}
                data-testid={`nav-optimar-${item.name
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-amber-50 text-amber-800 font-medium'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.name}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <Avatar>
            <AvatarImage src="https://customer-assets.emergentagent.com/job_farm-to-table-135/artifacts/xpjs1q1r_IMG_5536.jpeg" />
            <AvatarFallback>
              {user.name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {user.name}
            </p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
          </div>
        </div>
        <div className="mb-3">
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(
              user.role
            )}`}
          >
            {getRoleName(user.role)}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onLogout}
          className="w-full"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" data-testid="dashboard-layout">
      {/* Desktop sidebar (≥ lg) */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col">
        <SidebarBody />
      </aside>

      {/* Mobile drawer (< lg) */}
      {mobileNavOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
            onClick={() => setMobileNavOpen(false)}
            data-testid="mobile-nav-backdrop"
            aria-hidden="true"
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[80vw] flex-col bg-white border-r border-slate-200 shadow-xl lg:hidden"
            data-testid="mobile-nav-drawer"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <span className="text-sm font-semibold text-slate-700">Menu</span>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close menu"
                data-testid="mobile-nav-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarBody />
          </aside>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3 sm:px-6 lg:px-8 lg:py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 lg:hidden"
                aria-label="Open menu"
                data-testid="mobile-nav-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
              <p className="truncate text-sm font-medium text-slate-700">
                Supply Chain Portal
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="px-2 py-1 sm:px-3 bg-emerald-50 border border-emerald-200 rounded-full text-[10px] sm:text-xs font-medium text-emerald-700">
                <span className="hidden sm:inline">✓ All Systems Operational</span>
                <span className="sm:hidden">✓ Online</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>

        {/* Global platform-ownership footer (every page) */}
        <PlatformFooter variant="global" includeDemoNotice={false} />
      </div>
    </div>
  );
};

export default DashboardLayout;
